'use client';

/**
 * ShareButton — product-detail share affordance (M-10).
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item M-10
 *           (share button on product detail page).
 *
 * Renders a `<Share2 />` trigger button that opens a dropdown with
 * 4 share options: WhatsApp, X (Twitter), Facebook, + Copy link.
 *
 * Pre-filled share text (per the M-10 spec):
 *   - WhatsApp: "Mira este producto en Profileia: {title} - {url}"
 *     (full text embedded in the wa.me link — the user picks the
 *     contact in WhatsApp's native picker).
 *   - X (Twitter): `text="Mira este producto en Profileia: {title}"`
 *     + `url={url}` (X's intent endpoint accepts both params + merges
 *     them into a single tweet body).
 *   - Facebook: `u={url}` only — Facebook's sharer scrapes the page's
 *     OG meta tags for the title/description/image (the product detail
 *     page sets these via `generateMetadata`).
 *   - Copy link: `navigator.clipboard.writeText(url)` + sonner toast
 *     "Enlace copiado" (fallback to `window.prompt` on insecure
 *     contexts / older browsers).
 *
 * §6 Anti-Invención:
 *   - NO `as any` (P-041). NO `console.*` (P-042).
 *   - ARIA (P-044): the trigger has `aria-label` "Compartir producto";
 *     each item has `aria-label` with the platform name.
 *   - The `<Share2 />` icon is the M-10 spec'd lucide-react icon.
 *
 * Cross-domain note (CR-CHECKLIST §H5): `conference/SocialShareBar.tsx`
 * is a similar concept but lives in the conference domain (TikTok-style
 * inline bar for the spectator view — different platforms, different
 * prefix text, no dropdown). Consolidating them would mix domains
 * (§I4 — un módulo = un dominio) + require Founder approval. Flagged
 * for the Founder in the worklog; not consolidated in this PR.
 */

import { useCallback, useState } from 'react';
import { Share2, MessageCircle, Twitter, Facebook, Link2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export interface ShareButtonProps {
  /** Product title — used in the pre-filled share text. */
  title: string;
  /** Absolute public URL of the product detail page. */
  url: string;
  /** Optional className for the trigger button (e.g. 'ml-auto' to
   *  right-align inside a flex row). */
  className?: string;
}

const WHATSAPP_LABEL = 'WhatsApp';
const X_LABEL = 'X (Twitter)';
const FACEBOOK_LABEL = 'Facebook';
const COPY_LABEL = 'Copiar enlace';

export function ShareButton({ title, url, className }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const openShare = useCallback((href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer,width=600,height=600');
  }, []);

  const handleWhatsApp = useCallback(() => {
    const text = `Mira este producto en Profileia: ${title} - ${url}`;
    openShare(`https://wa.me/?text=${encodeURIComponent(text)}`);
  }, [title, url, openShare]);

  const handleX = useCallback(() => {
    const text = `Mira este producto en Profileia: ${title}`;
    openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`);
  }, [title, url, openShare]);

  const handleFacebook = useCallback(() => {
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
  }, [url, openShare]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Copiado', { description: 'Enlace copiado al portapapeles.' });
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      // Clipboard API unavailable (insecure context / older browser).
      // Fallback: prompt the user to copy manually (same pattern as
      // `conference/SocialShareBar.tsx`).
      window.prompt('Copia el enlace:', url);
    }
  }, [url]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5', className)}
          aria-label="Compartir producto"
        >
          <Share2 className="size-4" aria-hidden="true" />
          <span>Compartir</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleWhatsApp} aria-label={`Compartir en ${WHATSAPP_LABEL}`}>
          <MessageCircle className="size-4" aria-hidden="true" />
          {WHATSAPP_LABEL}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleX} aria-label={`Compartir en ${X_LABEL}`}>
          <Twitter className="size-4" aria-hidden="true" />
          {X_LABEL}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleFacebook} aria-label={`Compartir en ${FACEBOOK_LABEL}`}>
          <Facebook className="size-4" aria-hidden="true" />
          {FACEBOOK_LABEL}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopy} aria-label={COPY_LABEL}>
          {copied ? <Check className="size-4 text-emerald-600" aria-hidden="true" /> : <Link2 className="size-4" aria-hidden="true" />}
          {copied ? 'Copiado' : COPY_LABEL}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
